import { AfterViewInit, Component, ViewEncapsulation, OnInit } from '@angular/core';
import { trigger, style, transition, animate } from '@angular/animations';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { InvoiceService } from 'src/app/services/invoice/invoice.service';
import { UserService } from 'src/app/services/user/user.service';
import { ClientService } from 'src/app/services/user/client.service';
import { ShipService } from 'src/app/services/ship/ship.service';
import { PortService } from 'src/app/services/port/port.service';
import { GlobalStateService } from 'src/app/services/global/global-state.service';
import { Invoice } from 'src/app/shared/models/invoice.interface';
import { PaginatedResponse, PaginationInfo } from 'src/app/shared/models';
import Swal from 'sweetalert2';
import { GeneralService } from 'src/app/services/general/general.service';
import { Router } from '@angular/router';
declare var KTMenu: any;

// Define filter interface
interface CommissionFilters {
  sales_agent?: any;
  sales_agent_id?: string;
  client?: any;
  client_id?: string;
  ship?: any;
  ship_id?: string;
  sailing_from_date?: string;
  sailing_to_date?: string;
  arrival_from_date?: string;
  arrival_to_date?: string;
  invoice_from_date?: string;
  invoice_to_date?: string;
  sales_commission_from_date?: string;
  sales_commission_to_date?: string;
  stock_number?: string;
  destination_country?: string;
  destination_port?: string;
}

@Component({
  selector: 'app-commission',
  templateUrl: './commission.component.html',
  styleUrls: ['./commission.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({ height: '0px', opacity: 0, overflow: 'hidden' }),
        animate('300ms ease-in-out', style({ height: '*', opacity: 1 }))
      ]),
      transition(':leave', [
        style({ height: '*', opacity: 1, overflow: 'hidden' }),
        animate('300ms ease-in-out', style({ height: '0px', opacity: 0 }))
      ])
    ])
  ]
})
export class CommissionComponent extends DestructorComponent implements OnInit, AfterViewInit {

  // Data properties
  invoices: Invoice[] = [];
  paginationInfo: PaginationInfo = {
    currentPage: 1,
    perPage: 20,
    total: 0,
    lastPage: 1,
    firstPage: 1
  };

  // State properties
  isLoading: boolean = false;
  searchTerm: string = '';
  currentPage: number = 1;
  selectedStatus: string = 'pending';
  selectedCommissionStatus: string = 'pending';
  private searchTimeout: any;

  // Filter properties
  showFilters: boolean = false;
  filters: CommissionFilters = {};

  // Sales Agent search properties
  salesAgentSearchTerm: string = '';
  selectedSalesAgent: any = null;
  salesAgentSearchResults: any[] = [];
  showSalesAgentDropdown: boolean = false;
  isSalesAgentSearching: boolean = false;
  private salesAgentSearchSubject = new Subject<string>();

  // Client search properties
  clientSearchTerm: string = '';
  selectedClient: any = null;
  clientSearchResults: any[] = [];
  showClientDropdown: boolean = false;
  isClientSearching: boolean = false;
  private clientSearchSubject = new Subject<string>();

  // Ship search properties
  shipSearchTerm: string = '';
  selectedShip: any = null;
  shipSearchResults: any[] = [];
  showShipDropdown: boolean = false;
  isShipSearching: boolean = false;
  private shipSearchSubject = new Subject<string>();

  // Country and Port properties
  countries: any[] = [];
  ports: any[] = [];
  isPortsDisabled: boolean = true;

  // Invoice selection properties
  selectedInvoices: Set<string> = new Set();

  // Form data management for editable fields
  formData: Map<string, { sales_commission?: number; sales_commission_date?: string }> = new Map();
  hasChanges: boolean = false;
  isExportingExcel: boolean = false;

  constructor(
    private invoiceService: InvoiceService,
    private userService: UserService,
    private clientService: ClientService,
    private shipService: ShipService,
    private generalService: GeneralService,
    private portService: PortService,
    private _router:Router,
    private globalStateService: GlobalStateService
  ) {
    super();
    this.setupSearchDebounce();
  }

  ngOnInit(): void {
    this.loadInvoices(1, '', { commission_status: 'pending' });
    this.loadCountries();
  }

  ngAfterViewInit(): void {
    // Don't initialize DataTables since we're using Angular for data management
    // this.datatableService.init('kt_table_invoices');

    // Initialize menus after view is initialized
    this.initializeMenus();
  }

  loadInvoices(page: number = 1, search: string = '', filters: Record<string, any> = {}): void {
    this.isLoading = true;
    this.currentPage = page;

    this.subscriptions.push(
      this.invoiceService.GetData(page.toString(), search, filters).subscribe({
        next: (response: PaginatedResponse<Invoice>) => {
          if (response.status && response.data) {
            this.invoices = response.data.data || [];
            this.paginationInfo = response.data.pagination;
            this.currentPage = response.data.pagination.currentPage;
          } else {
            this.invoices = [];
            this.resetPagination();
          }
          this.isLoading = false;
          // Initialize KTMenu instances after data is loaded
          this.refreshMenus();
        },
        error: (error) => {
          console.error('Error loading invoices:', error);
          this.invoices = [];
          this.resetPagination();
          this.isLoading = false;
          // Initialize KTMenu instances even on error
          this.refreshMenus();
        }
      })
    );
  }

  private resetPagination(): void {
    this.paginationInfo = {
      currentPage: 1,
      perPage: 20,
      total: 0,
      lastPage: 1,
      firstPage: 1
    };
  }

  onPageChange(page: number): void {
    this.loadInvoices(page, this.searchTerm);
  }

  onSearch(searchTerm: string): void {
    this.searchTerm = searchTerm;
    this.loadInvoices(1, searchTerm);
  }

  onSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;

    // Clear previous timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    // Debounce search by 300ms
    this.searchTimeout = setTimeout(() => {
      this.onSearch(target.value);
    }, 300);
  }

  onStatusFilter(status: string): void {
    this.selectedStatus = status;
    const filters: Record<string, any> = {};
    if (status && status !== 'all') {
      filters['status'] = status;
    }
    this.loadInvoices(1, this.searchTerm, filters);
  }

  onResetFilters(): void {
    this.searchTerm = '';
    this.selectedStatus = '';
    this.loadInvoices(1);
  }

  onCommissionStatusFilter(status: string): void {
    this.selectedCommissionStatus = status;
    this.loadInvoices(1, '', { commission_status: status });
  }

  // Invoice selection methods
  onInvoiceSelect(invoiceId: string, event: any): void {
    if (event.target.checked) {
      this.selectedInvoices.add(invoiceId);
    } else {
      this.selectedInvoices.delete(invoiceId);
    }
  }

  onSelectAll(event: any): void {
    if (event.target.checked) {
      this.invoices.forEach(invoice => this.selectedInvoices.add(invoice._id));
    } else {
      this.selectedInvoices.clear();
    }
  }

  isInvoiceSelected(invoiceId: string): boolean {
    return this.selectedInvoices.has(invoiceId);
  }

  isAllSelected(): boolean {
    return this.invoices.length > 0 && this.selectedInvoices.size === this.invoices.length;
  }

  isIndeterminate(): boolean {
    return this.selectedInvoices.size > 0 && this.selectedInvoices.size < this.invoices.length;
  }

  onCopySMJNumbers(): void {
    if (this.selectedInvoices.size === 0) {
      return;
    }

    // Get selected invoices
    const selectedInvoiceIds = Array.from(this.selectedInvoices);
    const selectedInvoiceData = this.invoices.filter(invoice =>
      selectedInvoiceIds.includes(invoice._id)
    );

    // Extract car_no (SMJ#) from selected invoices
    const smjNumbers = selectedInvoiceData
      .map(invoice => invoice.vehicle?.car_no)
      .filter(carNo => carNo) // Filter out null/undefined values
      .join(',');

    if (!smjNumbers) {
      Swal.fire({
        icon: 'warning',
        title: 'No SMJ Numbers',
        text: 'Selected invoices do not have SMJ numbers.',
        confirmButtonText: 'OK'
      });
      return;
    }

    // Copy to clipboard
    navigator.clipboard.writeText(smjNumbers).then(() => {
      Swal.fire({
        icon: 'success',
        title: 'Copied!',
        text: `${selectedInvoiceData.length} SMJ number(s) copied to clipboard.`,
        timer: 2000,
        showConfirmButton: false
      });
    }).catch(err => {
      console.error('Failed to copy to clipboard:', err);
      Swal.fire({
        icon: 'error',
        title: 'Copy Failed',
        text: 'Failed to copy SMJ numbers to clipboard.',
        confirmButtonText: 'OK'
      });
    });
  }

  // Invoice detail modal properties
  selectedInvoiceForDetail: Invoice | null = null;
  selectedInvoiceIdForDetail: string = '';

  // Sales commission modal properties
  selectedInvoiceIdForCommission: string = '';
  selectedInvoiceNumberForCommission: string = '';



  onClearSelection(): void {
    this.selectedInvoices.clear();
  }

  // Helper methods for displaying data
  formatDate(dateString: string | null | undefined): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  formatCurrency(amount: number, currency: string | undefined = 'USD'): string {
    if (!amount) return '-';
    return `${amount.toLocaleString()} ${currency || 'USD'}`;
  }

  calculatePaidPercentage(paid: number, total: number): string {
    if (!total || total === 0) return '0%';
    const percentage = (paid / total) * 100;
    return `${Math.round(percentage)}%`;
  }

  trackByInvoiceId(_index: number, invoice: Invoice): string {
    return invoice._id;
  }

  // Pagination helper methods
  getPageNumbers(): number[] {
    const pages: number[] = [];
    const maxPagesToShow = 5;
    const currentPage = this.paginationInfo.currentPage;
    const lastPage = this.paginationInfo.lastPage;

    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(lastPage, startPage + maxPagesToShow - 1);

    // Adjust start page if we're near the end
    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  }

  // Math helper for template
  get Math() {
    return Math;
  }

  // Initialize KTMenu instances
  private initializeMenus(): void {
    setTimeout(() => {
      if (typeof KTMenu !== 'undefined') {
        KTMenu.createInstances();
      }
    }, 100);
  }

  // Refresh menus after data load
  private refreshMenus(): void {
    setTimeout(() => {
      if (typeof KTMenu !== 'undefined') {
        KTMenu.createInstances();
      }
    }, 50);
  }

  invoiceStatus(invoiceId: string, status: string) {


    Swal.fire({
      title: `${status == "approved" ? "Un Approve" : "Approve"} Invoice?`,
      text: `Are you sure you want to ${status == "approved" ? "un approve" : "approve"} this invoice.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: `Yes, ${status == "approved" ? "un approve" : "approve"} invoice!`,
      cancelButtonText: 'No, cancel',
      buttonsStyling: false,
      customClass: {
        confirmButton: 'btn fw-bold btn-primary',
        cancelButton: 'btn fw-bold btn-active-light-primary',
      },
    }).then((result) => {
      if (result.isConfirmed) {

        status = status == "approved" ? "pending" : "approved";
        this.invoiceService.UpdateData(invoiceId, { status }).subscribe({
          next: (response: any) => {
            if (response.status) {
              this.loadInvoices(this.currentPage);
            }
          },
          error: (error) => {
            console.error('Error updating invoice status:', error);
          }
        });
      }
    });



  }

  // Method to open invoice detail modal
  openInvoiceDetailModal(invoice: Invoice): void {
    this.selectedInvoiceForDetail = invoice;
    this.selectedInvoiceIdForDetail = invoice._id;

    // Open the modal using Bootstrap
    const modalElement = document.getElementById('kt_modal_invoice_detail');
    if (modalElement) {
      const modal = new (window as any).bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  // Method to open add sales commission modal
  openAddCommissionModal(invoice: Invoice): void {
    this.selectedInvoiceIdForCommission = invoice._id;
    this.selectedInvoiceNumberForCommission = invoice.invoice_number;

    // Open the modal using Bootstrap
    const modalElement = document.getElementById('kt_modal_add_sales_commission');
    if (modalElement) {
      const modal = new (window as any).bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  // Method called when commission is added successfully
  onCommissionAdded(): void {
    // Reload the invoices to show updated data
    this.loadInvoices(this.currentPage, this.searchTerm, { commission_status: this.selectedCommissionStatus });
  }

  // Setup search debounce for searchable inputs
  private setupSearchDebounce(): void {
    // Sales Agent search debounce
    this.subscriptions.push(
      this.salesAgentSearchSubject.pipe(
        debounceTime(300),
        distinctUntilChanged()
      ).subscribe(searchTerm => {
        this.searchSalesAgents(searchTerm);
      })
    );

    // Client search debounce
    this.subscriptions.push(
      this.clientSearchSubject.pipe(
        debounceTime(300),
        distinctUntilChanged()
      ).subscribe(searchTerm => {
        this.searchClients(searchTerm);
      })
    );

    // Ship search debounce
    this.subscriptions.push(
      this.shipSearchSubject.pipe(
        debounceTime(300),
        distinctUntilChanged()
      ).subscribe(searchTerm => {
        this.searchShips(searchTerm);
      })
    );
  }

  // Filter methods
  toggleFilters(): void {
    this.showFilters = !this.showFilters;
  }

  applyFilters(): void {
    // Set IDs from selected objects
    if (this.filters.sales_agent) {
      this.filters.sales_agent_id = this.filters.sales_agent._id;
    }
    if (this.filters.client) {
      this.filters.client_id = this.filters.client._id;
    }
    if (this.filters.ship) {
      this.filters.ship_id = this.filters.ship._id;
    }

    console.log('Applied filters:', this.filters);
    this.currentPage = 1;
    this.loadInvoices(1, this.searchTerm, { ...this.filters, commission_status: this.selectedCommissionStatus });
  }

  resetFilters(): void {
    this.filters = {};
    this.salesAgentSearchTerm = '';
    this.clientSearchTerm = '';
    this.shipSearchTerm = '';
    this.selectedSalesAgent = null;
    this.selectedClient = null;
    this.selectedShip = null;
    this.ports = [];
    this.isPortsDisabled = true;
    this.showFilters = false;
    this.currentPage = 1;
    this.loadInvoices(1, this.searchTerm, { commission_status: this.selectedCommissionStatus });
  }

  // Load countries method
  loadCountries(): void {
    this.subscriptions.push(
      this.globalStateService.GetCountries().subscribe({
        next: (countries: any) => {
          this.countries = countries || [];
        },
        error: (error: any) => {
          console.error('Error loading countries:', error);
        }
      })
    );
  }

  // Sales Agent search methods
  onSalesAgentSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.salesAgentSearchTerm = target.value;
    this.selectedSalesAgent = null;
    this.salesAgentSearchSubject.next(this.salesAgentSearchTerm);
    this.showSalesAgentDropdown = true;
  }

  onSalesAgentInputFocus(): void {
    if (this.salesAgentSearchResults.length > 0) {
      this.showSalesAgentDropdown = true;
    }
  }

  onSalesAgentInputBlur(): void {
    setTimeout(() => {
      this.showSalesAgentDropdown = false;
    }, 200);
  }

  private searchSalesAgents(searchTerm: string): void {
    if (!searchTerm || searchTerm.length < 2) {
      this.salesAgentSearchResults = [];
      this.showSalesAgentDropdown = false;
      return;
    }

    this.isSalesAgentSearching = true;
    this.subscriptions.push(
      this.userService.GetData('1', searchTerm, { paginate: false, role_type: 30 }).subscribe({
        next: (response: PaginatedResponse<any>) => {
          this.isSalesAgentSearching = false;
          if (response.status && response.data) {
            this.salesAgentSearchResults = response.data.data || response.data || [];
            this.showSalesAgentDropdown = this.salesAgentSearchResults.length > 0;
          } else {
            this.salesAgentSearchResults = [];
            this.showSalesAgentDropdown = false;
          }
        },
        error: (error: any) => {
          this.isSalesAgentSearching = false;
          console.error('Error searching sales agents:', error);
          this.salesAgentSearchResults = [];
          this.showSalesAgentDropdown = false;
        }
      })
    );
  }

  selectSalesAgent(salesAgent: any): void {
    this.selectedSalesAgent = salesAgent;
    this.filters.sales_agent = salesAgent;
    this.salesAgentSearchTerm = this.getSalesAgentDisplayName(salesAgent);
    this.showSalesAgentDropdown = false;
  }

  getSalesAgentDisplayName(salesAgent: any): string {
    if (salesAgent.name && salesAgent.company) {
      return `${salesAgent.name}, ${salesAgent.company}`;
    }
    return `${salesAgent.name || ''} ${salesAgent.last_name || ''}`.trim() || salesAgent.email || 'Unknown Sales Agent';
  }

  // Client search methods
  onClientSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.clientSearchTerm = target.value;
    this.selectedClient = null;
    this.clientSearchSubject.next(this.clientSearchTerm);
    this.showClientDropdown = true;
  }

  onClientInputFocus(): void {
    if (this.clientSearchResults.length > 0) {
      this.showClientDropdown = true;
    }
  }

  onClientInputBlur(): void {
    setTimeout(() => {
      this.showClientDropdown = false;
    }, 200);
  }

  private searchClients(searchTerm: string): void {
    if (!searchTerm || searchTerm.length < 2) {
      this.clientSearchResults = [];
      this.showClientDropdown = false;
      return;
    }

    this.isClientSearching = true;
    this.subscriptions.push(
      this.clientService.GetData('1', searchTerm, { paginate: false }).subscribe({
        next: (response: PaginatedResponse<any>) => {
          this.isClientSearching = false;
          if (response.status && response.data) {
            this.clientSearchResults = response.data.data || response.data || [];
            this.showClientDropdown = this.clientSearchResults.length > 0;
          } else {
            this.clientSearchResults = [];
            this.showClientDropdown = false;
          }
        },
        error: (error: any) => {
          this.isClientSearching = false;
          console.error('Error searching clients:', error);
          this.clientSearchResults = [];
          this.showClientDropdown = false;
        }
      })
    );
  }

  selectClient(client: any): void {
    this.selectedClient = client;
    this.filters.client = client;
    this.clientSearchTerm = this.getClientDisplayName(client);
    this.showClientDropdown = false;
  }

  getClientDisplayName(client: any): string {
    if (client.name && client.email) {
      return `${client.name} (${client.email})`;
    }
    return client.name || client.email || 'Unknown Client';
  }

  // Ship search methods
  onShipSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.shipSearchTerm = target.value;
    this.selectedShip = null;
    this.shipSearchSubject.next(this.shipSearchTerm);
    this.showShipDropdown = true;
  }

  onShipInputFocus(): void {
    if (this.shipSearchResults.length > 0) {
      this.showShipDropdown = true;
    }
  }

  onShipInputBlur(): void {
    setTimeout(() => {
      this.showShipDropdown = false;
    }, 200);
  }

  private searchShips(searchTerm: string): void {
    if (!searchTerm || searchTerm.length < 2) {
      this.shipSearchResults = [];
      this.showShipDropdown = false;
      return;
    }

    this.isShipSearching = true;
    this.subscriptions.push(
      this.shipService.GetData('1', searchTerm).subscribe({
        next: (response: PaginatedResponse<any>) => {
          this.isShipSearching = false;
          if (response.status && response.data && response.data.data) {
            this.shipSearchResults = response.data.data;
            this.showShipDropdown = this.shipSearchResults.length > 0;
          } else {
            this.shipSearchResults = [];
            this.showShipDropdown = false;
          }
        },
        error: (error: any) => {
          this.isShipSearching = false;
          console.error('Error searching ships:', error);
          this.shipSearchResults = [];
          this.showShipDropdown = false;
        }
      })
    );
  }

  selectShip(ship: any): void {
    this.selectedShip = ship;
    this.filters.ship = ship;
    this.shipSearchTerm = this.getShipDisplayName(ship);
    this.showShipDropdown = false;
  }

  getShipDisplayName(ship: any): string {
    return `${ship.ship_id || ship.name}, ${ship.name} - ${ship.company} (${ship.voyage_no})`;
  }

  // Country and Port methods
  onCountryChange(): void {
    if (this.filters.destination_country) {
      this.isPortsDisabled = false;
      this.loadPorts(this.filters.destination_country);
    } else {
      this.isPortsDisabled = true;
      this.ports = [];
      this.filters.destination_port = '';
    }
  }

  private loadPorts(countryCode: string): void {
    this.subscriptions.push(
      this.portService.GetData('1', '', { country_code: countryCode, paginate: false }).subscribe({
        next: (response: PaginatedResponse<any>) => {
          if (response.status && response.data) {
            this.ports = response.data.data || response.data || [];
          } else {
            this.ports = [];
          }
        },
        error: (error: any) => {
          console.error('Error loading ports:', error);
          this.ports = [];
        }
      })
    );
  }

  // Form value management methods
  getFormValue(invoiceId: string, field: 'sales_commission' | 'sales_commission_date'): any {
    const invoice = this.invoices.find(inv => inv._id === invoiceId);
    if (!invoice) return '';

    // Check if there's a form value first
    const formValue = this.formData.get(invoiceId);
    if (formValue && formValue[field] !== undefined) {
      return formValue[field];
    }

    // Return the original invoice value
    if (field === 'sales_commission_date') {
      const dateValue = invoice.sales_commission_date;
      if (dateValue) {
        // Convert date to YYYY-MM-DD format for date input
        const date = new Date(dateValue);
        return date.toISOString().split('T')[0];
      }
      return '';
    }

    return invoice[field] || '';
  }

  setFormValue(invoiceId: string, field: 'sales_commission' | 'sales_commission_date', value: any): void {
    const invoice = this.invoices.find(inv => inv._id === invoiceId);
    if (!invoice) return;

    // Get or create form data for this invoice
    let formValue = this.formData.get(invoiceId);
    if (!formValue) {
      formValue = {};
      this.formData.set(invoiceId, formValue);
    }

    // Update the field
    formValue[field] = value;

    // Check if there are any changes
    this.checkForChanges();
  }

  private checkForChanges(): void {
    this.hasChanges = false;

    for (const [invoiceId, formValue] of this.formData.entries()) {
      const invoice = this.invoices.find(inv => inv._id === invoiceId);
      if (!invoice) continue;

      // Check if sales_commission has changed
      if (formValue.sales_commission !== undefined) {
        const originalValue = invoice.sales_commission || 0;
        const newValue = formValue.sales_commission;
        if (originalValue !== newValue && newValue !== null && newValue !== undefined) {
          this.hasChanges = true;
          break;
        }
      }

      // Check if sales_commission_date has changed
      if (formValue.sales_commission_date !== undefined) {
        const originalDate = invoice.sales_commission_date
          ? new Date(invoice.sales_commission_date).toISOString().split('T')[0]
          : '';
        const newDate = formValue.sales_commission_date;
        if (originalDate !== newDate && newDate !== '') {
          this.hasChanges = true;
          break;
        }
      }
    }
  }

  onSave(): void {
    if (!this.hasChanges) {
      return;
    }

    // Prepare updates array
    const updates: Array<{ invoice_id: string; sales_commission?: number; sales_commission_date?: string }> = [];

    for (const [invoiceId, formValue] of this.formData.entries()) {
      const invoice = this.invoices.find(inv => inv._id === invoiceId);
      if (!invoice) continue;

      const update: any = { invoice_id: invoiceId };
      let hasUpdate = false;

      // Check sales_commission
      if (formValue.sales_commission !== undefined && formValue.sales_commission !== null) {
        const originalValue = invoice.sales_commission || 0;
        if (originalValue !== formValue.sales_commission) {
          update.sales_commission = formValue.sales_commission;
          hasUpdate = true;
        }
      }

      // Check sales_commission_date
      if (formValue.sales_commission_date !== undefined && formValue.sales_commission_date !== '') {
        const originalDate = invoice.sales_commission_date
          ? new Date(invoice.sales_commission_date).toISOString().split('T')[0]
          : '';
        if (originalDate !== formValue.sales_commission_date) {
          update.sales_commission_date = formValue.sales_commission_date;
          hasUpdate = true;
        }
      }

      if (hasUpdate) {
        updates.push(update);
      }
    }

    if (updates.length === 0) {
      Swal.fire({
        icon: 'info',
        title: 'No Changes',
        text: 'No changes to save.',
        confirmButtonText: 'OK'
      });
      return;
    }

    this.isLoading = true;

    // Call the API to update invoices
    this.subscriptions.push(
      this.invoiceService.bulkUpdateCommission(updates).subscribe({
        next: (response: any) => {
          this.isLoading = false;
          if (response.status) {
            Swal.fire({
              icon: 'success',
              title: 'Success',
              text: 'Commission data updated successfully.',
              confirmButtonText: 'OK'
            });

            // Clear form data and reload invoices
            this.formData.clear();
            this.hasChanges = false;
            this.loadInvoices(this.currentPage, this.searchTerm, this.buildFilters());
          } else {
            Swal.fire({
              icon: 'error',
              title: 'Error',
              text: response.message || 'Failed to update commission data.',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error: any) => {
          this.isLoading = false;
          console.error('Error updating commission data:', error);
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'An error occurred while updating commission data.',
            confirmButtonText: 'OK'
          });
        }
      })
    );
  }

  exportExcel(): void {
    this.isExportingExcel = true;

    const filtersObj = this.buildFilters();
    filtersObj.type = 'invoice';
    filtersObj.excel_name = 'Commission Report';
    filtersObj.invoice_type = 'commission';

    this.subscriptions.push(
      this.generalService.generateExcelPost(filtersObj).subscribe({
        next: (response: any) => {
          this.isExportingExcel = false;
          console.log('Excel export response:', response);

          if (response.status && response.data && response.data.url) {
            // Open the Excel file URL in a new tab
            window.open(response.data.url, '_blank');
          } else {
            // Show error message if no URL is returned
            Swal.fire({
              text: 'Failed to generate Excel file. No URL received.',
              icon: "error",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn fw-bold btn-primary",
              }
            });
          }
        },
        error: (error: any) => {
          this.isExportingExcel = false;
          console.error('Error exporting Excel:', error);
          Swal.fire({
            text: 'An error occurred while exporting Excel file.',
            icon: "error",
            buttonsStyling: false,
            confirmButtonText: "Ok, got it!",
            customClass: {
              confirmButton: "btn fw-bold btn-primary",
            }
          });
        }
      })
    );
  }

  private buildFilters(): Record<string, any> {
    const filters: Record<string, any> = {
      commission_status: this.selectedCommissionStatus
    };

    if (this.filters.sales_agent_id) {
      filters.sales_agent_id = this.filters.sales_agent_id;
    }

    if (this.filters.client_id) {
      filters.client_id = this.filters.client_id;
    }

    if (this.filters.ship_id) {
      filters.ship_id = this.filters.ship_id;
    }

    if (this.filters.sailing_from_date) {
      filters.sailing_from_date = this.filters.sailing_from_date;
    }

    if (this.filters.sailing_to_date) {
      filters.sailing_to_date = this.filters.sailing_to_date;
    }

    if (this.filters.arrival_from_date) {
      filters.arrival_from_date = this.filters.arrival_from_date;
    }

    if (this.filters.arrival_to_date) {
      filters.arrival_to_date = this.filters.arrival_to_date;
    }

    if (this.filters.invoice_from_date) {
      filters.invoice_from_date = this.filters.invoice_from_date;
    }

    if (this.filters.invoice_to_date) {
      filters.invoice_to_date = this.filters.invoice_to_date;
    }

    if (this.filters.sales_commission_from_date) {
      filters.sales_commission_from_date = this.filters.sales_commission_from_date;
    }

    if (this.filters.sales_commission_to_date) {
      filters.sales_commission_to_date = this.filters.sales_commission_to_date;
    }

    if (this.filters.stock_number) {
      filters.stock_number = this.filters.stock_number;
    }

    if (this.filters.destination_country) {
      filters.destination_country = this.filters.destination_country;
    }

    if (this.filters.destination_port) {
      filters.destination_port = this.filters.destination_port;
    }

    return filters;
  }

  viewDetailClient(client_id: any): void {
    if (client_id) {
      const url = this._router.serializeUrl(
        this._router.createUrlTree(['sales/client-manager', client_id])
      );

      window.open(url, '_blank');
    }
  }
}
